import { CashFlowConfig, CashFlowType } from '@ndvr/portfolio-api';

import { useListAccountGroupProfiles, useListCashflowConfigs } from 'views/portfolio/account-group-queries';

export interface CashFlow {
    id: string;
    amount: string;
    frequency: string;
    startDate: string;
    endDate?: string;
    occurrenceCount?: number;
    title: string;
}

export interface Account {
    id: string;
    name: string;
}

export interface LiquidityPlanningData {
    isLoading: boolean;
    withdrawals: CashFlow[];
    contributions: CashFlow[];
    cashFlowConfigs: CashFlowConfig[];
    accounts: Account[];
}

export const useLiquidityPlanningData = (
    accountGroupGUID: string,
    globalAccountGUID?: string,
): LiquidityPlanningData => {
    const {
        data: cashflowConfigs,
        isLoading: isCashflowConfigsLoading,
        isFetching: isCashflowConfigsFetching,
    } = useListCashflowConfigs(accountGroupGUID || '');
    const {
        data: accountGroupProfiles,
        isLoading: isAccountGroupProfilesLoading,
        isFetching: isAccountGroupProfilesFetching,
    } = useListAccountGroupProfiles(globalAccountGUID || '');

    const mapCashFlow = (config: CashFlowConfig): CashFlow => ({
        id: config.cashFlowConfigGUID,
        title: config.title.plainText,
        amount: config.amount,
        frequency: config.factorDateFrame?.frequencyType,
        startDate: config.factorDateFrame?.startDate,
        endDate: config.factorDateFrame?.endDate,
        occurrenceCount: config.factorDateFrame?.occurrenceCount,
    });

    const withdrawals =
        cashflowConfigs?.filter((config) => config.cashFlowType === CashFlowType.WITHDRAWAL).map(mapCashFlow) || [];

    const contributions =
        cashflowConfigs?.filter((config) => config.cashFlowType === CashFlowType.CONTRIBUTION).map(mapCashFlow) || [];

    const accounts: Account[] =
        accountGroupProfiles
            ?.map((profile) => profile.accounts)
            .flat()
            .filter((account) => account.accountGroupGUID === accountGroupGUID)
            .map((account) => ({
                id: account.accountGUID,
                name: account.accountName.plainText,
            })) || [];

    const isLoading =
        isCashflowConfigsLoading ||
        isAccountGroupProfilesLoading ||
        isCashflowConfigsFetching ||
        isAccountGroupProfilesFetching;

    return {
        isLoading,
        withdrawals,
        contributions,
        cashFlowConfigs: cashflowConfigs || [],
        accounts,
    };
};
